/**
 * templateConfigStore.ts
 * Persists configuration that belongs to a *template* rather than to a working
 * session: today the per-page display conditions.
 *
 * The key is the template hash, so reloading the same DOCX restores its rules
 * whatever Excel file is paired with it. The page count at save time is stored
 * alongside so a template that gained or lost sections can be flagged instead of
 * having its rules land on the wrong pages.
 */

import type { PageConditionMap } from "@/utils/pageCondition";

const STORAGE_PREFIX = "mini_printer_template_config_v1:";

export interface TemplateConfig {
  version: 1;
  savedAt: number;
  /** Number of Word sections the template had when the rules were written. */
  pageCount: number;
  /** Sheet the condition fields were picked from. */
  sheetName: string;
  pageConditions: PageConditionMap;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function templateConfigKey(templateHash: string): string {
  return `${STORAGE_PREFIX}${templateHash}`;
}

export function saveTemplateConfig(
  templateHash: string,
  config: Omit<TemplateConfig, "version" | "savedAt">,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage || !templateHash) return;
  try {
    const payload: TemplateConfig = { ...config, version: 1, savedAt: Date.now() };
    storage.setItem(templateConfigKey(templateHash), JSON.stringify(payload));
  } catch {
    // Quota or private-mode failures must never break the flow.
  }
}

export function loadTemplateConfig(
  templateHash: string,
  storage: StorageLike | null = defaultStorage()
): TemplateConfig | null {
  if (!storage || !templateHash) return null;
  try {
    const raw = storage.getItem(templateConfigKey(templateHash));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TemplateConfig;
    if (!parsed || parsed.version !== 1 || typeof parsed.pageConditions !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearTemplateConfig(
  templateHash: string,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage || !templateHash) return;
  try {
    storage.removeItem(templateConfigKey(templateHash));
  } catch {
    // ignore
  }
}

/**
 * Warning shown when the template no longer has the pages the config was
 * written against — the rules are kept but must be reviewed by the user.
 */
export function pageCountMismatchWarning(
  config: TemplateConfig | null,
  currentPageCount: number
): string | null {
  if (!config || config.pageCount === 0 || currentPageCount === 0) return null;
  if (config.pageCount === currentPageCount) return null;
  const hasRules = Object.values(config.pageConditions ?? {}).some(
    (rule) => (rule?.conditions?.length ?? 0) > 0
  );
  if (!hasRules) return null;
  return `Template hiện có ${currentPageCount} trang nhưng điều kiện đã lưu được cấu hình cho ${config.pageCount} trang — hãy kiểm tra lại từng trang trước khi in.`;
}
